import { useRef } from "react";
import type { ShapeCanvasObject } from "@refinement-canvas/domain";
import { useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";

interface Props {
  readonly object: ShapeCanvasObject;
}

// 図形オブジェクト（丸 / 四角 / 矢印）。SVG で描画する。
// クリックで選択、ドラッグで移動できる。SVG 自体に pointer events を付けて
// 内側 fill が transparent でもヒットを取れるよう pointerEvents="bounding-box" を付与する。
export function ShapeObject({ object }: Props): JSX.Element {
  const ref = useRef<SVGSVGElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  // 単一選択時だけでなく、選択集合（Ctrl+クリックや範囲指定での複数選択を含む）に
  // 入っていればハイライトする。
  const isSelected = useCanvasStore((s) => s.selectedObjectIds.includes(object.id));

  useObjectDrag({
    objectId: object.id,
    elementRef: ref as unknown as React.RefObject<HTMLElement | null>,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    disabled: activeTool === "draw-pen",
  });

  const stroke = isSelected ? "var(--color-accent)" : object.stroke ?? "#1a1a2e";

  return (
    <svg
      ref={ref}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="shape"
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        zIndex: object.zOrder,
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        transformOrigin: "center",
        pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
        cursor: "move",
        overflow: "visible",
      }}
      // リサイズ中に viewBox が更新されない瞬間でも、内部要素を常に SVG の表示寸法へ
      // 等比展開させたい。preserveAspectRatio="none" で縦横独立に伸縮し、
      // viewBox の値に依存せずドロップ後の最終図形と同じ見た目をプレビューする。
      viewBox={`0 0 ${object.w} ${object.h}`}
      preserveAspectRatio="none"
    >
      {object.shapeKind === "circle" ? (
        // 100% 基準で SVG 表示寸法に追従させ、ドラッグ中の枠と中身を一致させる。
        // stroke は vector-effect で非スケールに固定し、伸縮しても太さが均一に保たれる。
        <ellipse
          cx="50%"
          cy="50%"
          rx="50%"
          ry="50%"
          fill={object.fill ?? "transparent"}
          stroke={stroke}
          strokeWidth={isSelected ? 3 : 2}
          vectorEffect="non-scaling-stroke"
          pointerEvents="all"
        />
      ) : object.shapeKind === "rectangle" ? (
        // 矩形も 100% で SVG を埋めて、リサイズ中の輪郭が選択枠と一致するようにする。
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill={object.fill ?? "transparent"}
          stroke={stroke}
          strokeWidth={isSelected ? 3 : 2}
          vectorEffect="non-scaling-stroke"
          pointerEvents="all"
        />
      ) : (
        // 矢印は preserveAspectRatio="none" 下で viewBox を等比伸縮するため、
        // 内部座標は object.w / object.h の初期値ベースのままで概形が保たれる。
        // 矢じり先端の絶対サイズが多少伸縮するが、ドロップ後の最終形に近いゴースト表示となる。
        <g
          stroke={stroke}
          fill="none"
          strokeWidth={isSelected ? 3 : 2}
          vectorEffect="non-scaling-stroke"
          pointerEvents="all"
        >
          <line x1={4} y1={object.h / 2} x2={object.w - 12} y2={object.h / 2} />
          <polyline
            points={`${object.w - 12},${object.h / 2 - 6} ${object.w - 4},${object.h / 2} ${object.w - 12},${object.h / 2 + 6}`}
          />
        </g>
      )}
    </svg>
  );
}
